import { ProblemDetails } from "../models/Http";
import {
  DoorToOpen,
  OnBoardingStore,
  Store,
  StoreEvent,
  StorePickupBox,
  StoreWriteModel
} from "../models/Store";
import { get, post, put } from "./api";
import { err, ok, Result, Unit } from "../result";
import moment from "moment";

export const list = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Store>>
> => {
  const response = await get(`/v1/stores`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Store>);
  }

  return err(content as ProblemDetails);
};

export const listAll = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Store>>
> => {
  const response = await get(`/v1/stores/all`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Store>);
  }

  return err(content as ProblemDetails);
};

export const listByProductId = async (
  productId: string
): Promise<Result<ProblemDetails, ReadonlyArray<Store>>> => {
  const response = await get(`/v1/stores/list-by-productid/${productId}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Store>);
  }

  return err(content as ProblemDetails);
};

export const getById = async (
  storeId: string
): Promise<Result<ProblemDetails, Store>> => {
  const response = await get(`/v1/stores/${storeId}?includePickupBoxes=true`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as Store);
  }

  return err(content as ProblemDetails);
};

export const add = async (
  writeModel: StoreWriteModel
): Promise<Result<ProblemDetails, {}>> => {
  const response = await post("/v1/stores", writeModel);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const update = async (
  storeId: string,
  writeModel: StoreWriteModel
): Promise<Result<ProblemDetails, {}>> => {
  const response = await put(`/v1/stores/${storeId}`, writeModel);

  if (response.ok) {
    return ok({});
  }
  const content = await response.json();

  return err(content as ProblemDetails);
};

export const upsertOnboarding = async (
  model: OnBoardingStore
): Promise<Result<ProblemDetails, string>> => {
  const response = await put(`/v1/stores/onboarding`, model);

  const content = await response.json();

  if (response.ok) {
    return ok(content);
  }

  return err(content as ProblemDetails);
};

export const openDoor = async (
  storeId: string,
  doorToOpen: DoorToOpen
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/stores/${storeId}/open-door`, doorToOpen);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const getStoreControllerStatus = async (
  storeId: string
): Promise<Result<ProblemDetails, string>> => {
  const response = await get(`/v1/stores/${storeId}/get-controller-status`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as string);
  }

  return err(content as ProblemDetails);
};

export const syncAllPriceTags = async (
  storeId: string
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/stores/${storeId}/sync-all-price-tags`);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const listEvents = async (
  storeId: string,
  from: string | null,
  to: string | null
): Promise<ReadonlyArray<StoreEvent>> => {
  const response = await get(
    `/v1/stores/${storeId}/events?periodFrom=${encodeURIComponent(
      from ?? ""
    )}&periodTo=${encodeURIComponent(to ?? "")}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch events: ${response.statusText}`);
  }

  const content = await response.json();
  return content as ReadonlyArray<StoreEvent>;
};

export const getDoorOpeningEventsForStores = async (
  from: Date,
  to: Date,
  storeIds: string[] = []
): Promise<Result<ProblemDetails, ReadonlyArray<StoreEvent>>> => {
  let additionalQuery = "";

  storeIds.forEach(val => {
    additionalQuery = `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/stores/events?periodFrom=${moment(from).format("YYYY-MM-DD HH:mm:ss")}
    &periodTo=${moment(to).format("YYYY-MM-DD HH:mm:ss")}${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StoreEvent>);
  }

  return err(content as ProblemDetails);
};

export const listEmptyPickupBoxes = async (
  storeId: string
): Promise<Result<ProblemDetails, ReadonlyArray<StorePickupBox>>> => {
  const response = await get(`/v1/stores/${storeId}/empty-pickup-boxes`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StorePickupBox>);
  }

  return err(content as ProblemDetails);
};

export const stopAlarm = async (storeId: string) => {
  const response = await post(`/v1/stores/${storeId}/stop-alarm`);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return;
};
